// Eventos de la tienda hacia el pixel de Meta.
//
// Los componentes llaman SOLO a `track()` / `trackPurchase()`, nunca a `fbq`
// directo: si mañana se suma otro destino, no se tocan los componentes. Sin
// pixel cargado (local sin configurar, cliente excluido, QR de mesa, página de
// pago de paso) todo es un no-op. Nunca lanza: medir no puede romper la venta.
import type { Conversion } from "@/lib/tracking/conversionValue"

export type StorefrontEvent = "view_item" | "add_to_cart" | "begin_checkout" | "add_payment_info" | "purchase"
export type TrackItem = { id: string; name: string; price: number; quantity: number }
export type TrackData = { value: number; currency: "USD"; items: TrackItem[]; eventId?: string }

const META_EVENT: Record<StorefrontEvent, string> = {
  view_item: "ViewContent",
  add_to_cart: "AddToCart",
  begin_checkout: "InitiateCheckout",
  add_payment_info: "AddPaymentInfo",
  purchase: "Purchase",
}

type Fbq = ((...args: unknown[]) => void) & { queue?: unknown[]; callMethod?: (...a: unknown[]) => void }
type PixelWindow = { fbq?: Fbq; _fbq?: Fbq; __redyPixelId?: string }

function pixelWindow(): PixelWindow | null {
  return typeof window === "undefined" ? null : (window as unknown as PixelWindow)
}

const round2 = (n: number) => Math.round(n * 100) / 100

export function track(name: StorefrontEvent, data: TrackData): void {
  try {
    const w = pixelWindow()
    if (!w?.fbq) return
    const params = {
      content_ids: data.items.map((i) => i.id),
      contents: data.items.map((i) => ({ id: i.id, quantity: i.quantity, item_price: round2(i.price) })),
      content_type: "product",
      num_items: data.items.reduce((s, i) => s + i.quantity, 0),
      value: round2(data.value),
      currency: data.currency,
    }
    if (data.eventId) w.fbq("track", META_EVENT[name], params, { eventID: data.eventId })
    else w.fbq("track", META_EVENT[name], params)
  } catch {
    // medición: jamás afecta el pedido
  }
}

/** Líneas del carrito como ítems de evento: precio por unidad CON modificadores. */
export function trackItemsFromCart(
  cart: ReadonlyArray<{
    itemId: string
    name: string
    price: number
    quantity: number
    modifiers: ReadonlyArray<{ price_delta: number; quantity?: number }>
  }>,
): TrackItem[] {
  return cart.map((c) => ({
    id: c.itemId,
    name: c.name,
    price: round2(c.price + c.modifiers.reduce((m, mod) => m + mod.price_delta * (mod.quantity ?? 1), 0)),
    quantity: c.quantity,
  }))
}

export function cartValue(items: TrackItem[]): number {
  return round2(items.reduce((s, i) => s + i.price * i.quantity, 0))
}

const PURCHASE_SENT_PREFIX = "redy:purchaseSent:"

/**
 * Purchase del navegador. `eventID = orderId`: el mismo que mandará la
 * Conversions API desde el servidor, y Meta deduplica. El valor sale SOLO del
 * servidor (`conversion` de la respuesta): sin él no se manda nada, para que
 * los dos lados nunca discrepen. Una vez por orden aunque la confirmación se
 * vuelva a pintar (recarga, vuelta del pago de paso).
 */
export function trackPurchase(input: { orderId: string; conversion: Conversion | undefined; items: TrackItem[] }): void {
  try {
    if (!input.conversion || input.conversion.value <= 0) return
    const key = PURCHASE_SENT_PREFIX + input.orderId
    try {
      if (window.sessionStorage.getItem(key)) return
      window.sessionStorage.setItem(key, "1")
    } catch {
      // sin sessionStorage (modo privado/WebView): se manda igual; el eventID deduplica en Meta
    }
    track("purchase", { value: input.conversion.value, currency: "USD", items: input.items, eventId: input.orderId })
  } catch {
    // medición
  }
}

/**
 * El stub estándar de `fbq` que Meta publica, escrito en TS para no inyectar
 * un `<script>` con texto interpolado: encola llamadas hasta que carga
 * fbevents.js, que las procesa al llegar.
 */
export function installFbqStub(w: PixelWindow): void {
  if (w.fbq) return
  const n = function (...args: unknown[]) {
    if (n.callMethod) n.callMethod(...args)
    else n.queue!.push(args)
  } as Fbq & { push?: unknown; loaded?: boolean; version?: string }
  w.fbq = n
  if (!w._fbq) w._fbq = n
  n.push = n
  n.loaded = true
  n.version = "2.0"
  n.queue = []
}

/** init + PageView una sola vez por id (React StrictMode monta dos veces en dev). */
export function initMetaPixel(w: PixelWindow, pixelId: string): boolean {
  if (!/^[0-9]{10,20}$/.test(pixelId)) return false
  if (w.__redyPixelId === pixelId) return false
  installFbqStub(w)
  w.__redyPixelId = pixelId
  // Sin «configuración automática» de Meta: con ella fbevents.js escucha clics y
  // lee la página por su cuenta (eventos automáticos y advanced matching
  // automático), y en el checkout eso incluye los campos de nombre, teléfono y
  // correo. Solo sale lo que Redy manda explícitamente. Va ANTES de init.
  w.fbq!("set", "autoConfig", false, pixelId)
  w.fbq!("init", pixelId)
  w.fbq!("track", "PageView")
  return true
}

const FBEVENTS_SRC = "https://connect.facebook.net/en_US/fbevents.js"
const FBEVENTS_ID = "meta-pixel-fbevents"

/** Pide fbevents.js una sola vez (async). El stub ya encoló init + PageView. */
export function loadFbeventsScript(doc: {
  getElementById(id: string): unknown
  createElement(tag: "script"): { id: string; async: boolean; src: string }
  head: { appendChild(node: unknown): unknown }
}): boolean {
  if (doc.getElementById(FBEVENTS_ID)) return false
  const s = doc.createElement("script")
  s.id = FBEVENTS_ID
  s.async = true
  s.src = FBEVENTS_SRC
  doc.head.appendChild(s)
  return true
}
